// ============================================================
// Download Progress Overlay - Taska Ummu Qaseh
// Kad terapung menunjukkan bar kemajuan semua muat turun aktif
// (PDF / Excel / JSON) - dipasang sekali dalam App.js
// ============================================================

import React, { useEffect, useState } from 'react';
import { Download, FileText, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { subscribeDownloads, formatBytes } from '../../lib/downloadProgress';

const PhaseIcon = ({ phase }) => {
  if (phase === 'done') return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
  if (phase === 'error') return <XCircle className="w-5 h-5 text-red-500" />;
  if (phase === 'generating') return <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />;
  return <Download className="w-5 h-5 text-blue-500" />;
};

const phaseText = (d) => {
  if (d.phase === 'generating') return 'Menjana laporan...';
  if (d.phase === 'downloading') {
    if (d.percent !== null && d.percent !== undefined) return `Memuat turun... ${d.percent}%`;
    return `Memuat turun... ${formatBytes(d.loadedBytes)}`;
  }
  if (d.phase === 'done') return 'Selesai dimuat turun!';
  if (d.phase === 'error') return d.errorMsg || 'Gagal muat turun';
  return '';
};

const barColor = (d) => {
  if (d.phase === 'error') return 'bg-red-500';
  if (d.phase === 'done') return 'bg-emerald-500';
  if (d.phase === 'generating') return 'bg-purple-400';
  return 'bg-blue-500';
};

const DownloadProgressOverlay = () => {
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribeDownloads(setDownloads);
    return unsubscribe;
  }, []);

  if (!downloads.length) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-3 w-[92vw] max-w-sm"
      data-testid="download-progress-overlay"
    >
      {downloads.map((d) => {
        const isIndeterminate =
          d.phase === 'generating' || (d.phase === 'downloading' && (d.percent === null || d.percent === undefined));
        const widthPct = d.phase === 'done' ? 100 : d.percent || 0;
        return (
          <div
            key={d.id}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 animate-in slide-in-from-bottom-2"
            data-testid="download-progress-item"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                <PhaseIcon phase={d.phase} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-800 truncate flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="truncate">{d.label}</span>
                </p>
                <p className="text-xs text-gray-500 font-medium" data-testid="download-progress-status">
                  {phaseText(d)}
                  {d.phase === 'downloading' && d.totalBytes ? (
                    <span className="text-gray-400"> ({formatBytes(d.loadedBytes)} / {formatBytes(d.totalBytes)})</span>
                  ) : null}
                </p>
              </div>
              {d.percent !== null && d.percent !== undefined && d.phase !== 'generating' && (
                <span className="text-sm font-bold text-gray-700 shrink-0">{widthPct}%</span>
              )}
            </div>
            {/* Bar kemajuan */}
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              {isIndeterminate ? (
                <div className={`h-full w-1/3 rounded-full ${barColor(d)} dj-progress-indeterminate`} />
              ) : (
                <div
                  className={`h-full rounded-full transition-[width] duration-300 ease-out ${barColor(d)}`}
                  style={{ width: `${widthPct}%` }}
                />
              )}
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes dj-indeterminate {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(320%); }
        }
        .dj-progress-indeterminate {
          animation: dj-indeterminate 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default DownloadProgressOverlay;
